import type { Lesson } from "@core/grades/types";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowUpLeft, Clock3, Eye, Pin, Play, UserRound } from "lucide-react";
import { useModernHomeLesson } from "../../hooks/use-modern-home-lesson.hook";

function FeaturedLesson({ lesson }: { lesson: Lesson }) {
  const details = useModernHomeLesson(lesson);

  return (
    <Link
      to={details.href}
      className="group relative min-h-[24rem] overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-xl shadow-slate-950/10"
    >
      {details.image ? (
        <img
          src={details.image}
          alt={lesson.name}
          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/45 to-slate-950/5" />

      <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-black backdrop-blur">
          <Pin className="h-3.5 w-3.5 fill-current" />
          {trans("home.featuredLesson")}
        </span>
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition group-hover:scale-110">
          <Play className="h-4 w-4 fill-current" />
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs font-bold text-white/75">
          {details.subject ? <span>{details.subject}</span> : null}
          {details.duration ? (
            <span className="inline-flex items-center gap-1" dir="ltr">
              <Clock3 className="h-3.5 w-3.5" />
              {details.duration}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1" dir="ltr">
            <Eye className="h-3.5 w-3.5" />
            {details.views}
          </span>
        </div>
        <h2 className="max-w-3xl text-2xl font-black leading-tight sm:text-3xl">
          {lesson.name}
        </h2>
        <div className="mt-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2.5">
            {details.instructorImage ? (
              <img
                src={details.instructorImage}
                alt={details.instructorName}
                className="h-9 w-9 rounded-full object-cover ring-2 ring-white/40"
              />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <UserRound className="h-4 w-4" />
              </span>
            )}
            <span className="truncate text-sm font-bold">
              {details.instructorName}
            </span>
          </div>
          <strong className="shrink-0 rounded-full bg-white px-3 py-1.5 text-sm text-slate-950">
            {details.price}
          </strong>
        </div>
      </div>
    </Link>
  );
}

function CompactPinnedLesson({
  lesson,
  index,
}: {
  lesson: Lesson;
  index: number;
}) {
  const details = useModernHomeLesson(lesson);

  return (
    <Link
      to={details.href}
      className="group flex min-h-28 gap-3 rounded-[1.4rem] border border-slate-200 bg-white p-3 transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-lg dark:border-slate-800 dark:bg-slate-950"
    >
      <div className="relative h-24 w-28 shrink-0 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-900">
        {details.image ? (
          <img
            src={details.image}
            alt={lesson.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : null}
        <span className="absolute start-2 top-2 flex h-7 min-w-7 items-center justify-center rounded-lg bg-slate-950/75 px-1 text-[10px] font-black text-white">
          {String(index + 2).padStart(2, "0")}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col py-1">
        <span className="text-[11px] font-black text-violet-600 dark:text-violet-300">
          {details.subject || trans("home.pinnedLessons")}
        </span>
        <h3 className="mt-1 line-clamp-2 text-sm font-black leading-5 text-slate-950 dark:text-white">
          {lesson.name}
        </h3>
        <div className="mt-auto flex items-center justify-between gap-2 text-[11px] font-bold text-slate-500 dark:text-slate-400">
          <span className="truncate">{details.instructorName}</span>
          <ArrowUpLeft className="h-4 w-4 shrink-0 text-violet-500 ltr:rotate-90 rtl:rotate-0" />
        </div>
      </div>
    </Link>
  );
}

export function ModernHomeLessonSpotlight({
  lessons = [],
}: {
  lessons?: Lesson[];
}) {
  if (lessons.length === 0) return null;

  return (
    <section>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <span className="text-xs font-black uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
            {trans("home.teacherPicks")}
          </span>
          <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
            {trans("home.pinnedLessons")}
          </h2>
        </div>
        <Link
          to="/lessons"
          className="text-sm font-black text-violet-700 dark:text-violet-300"
        >
          {trans("home.viewAll")}
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(18rem,0.75fr)]">
        <FeaturedLesson lesson={lessons[0]} />
        <div className="grid content-start gap-3 sm:grid-cols-2 lg:grid-cols-1">
          {lessons.slice(1, 4).map((lesson, index) => (
            <CompactPinnedLesson
              key={lesson.id}
              lesson={lesson}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
